import React, { useCallback, useEffect, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';

/**
 * Admin → Deploy Activity page.
 *
 * A durable, forensic trail of everything that happens to deployed apps —
 * deploy / redeploy / delete / stop / start / enhance — so an app that "goes
 * missing" can be traced to who did what, when. Backed by the append-only
 * deployActivityStore (file locally, S3 in prod) via GET /api/admin/deploy-activity.
 *
 * Admin-gated server-side (LYRIC_ADMIN_EMAILS or the lyric-admins Okta group).
 */

// A stable colour per action so the eye can scan the log quickly.
const ACTION_STYLES = {
  deploy: { bg: '#e8f5e9', fg: '#1b5e20' },
  redeploy: { bg: '#e3f2fd', fg: '#0d47a1' },
  delete: { bg: '#ffebee', fg: '#b71c1c' },
  stop: { bg: '#fff3e0', fg: '#e65100' },
  start: { bg: '#e8f5e9', fg: '#1b5e20' },
  enhance: { bg: '#f3e5f5', fg: '#4a148c' },
};

function actionStyle(action) {
  return ACTION_STYLES[action] || { bg: '#eceff1', fg: '#37474f' };
}

function fmtWhen(at) {
  if (!at) return '—';
  try {
    const d = new Date(at);
    if (Number.isNaN(d.getTime())) return String(at);
    return d.toLocaleString();
  } catch {
    return String(at);
  }
}

export default function AdminDeployActivity() {
  const { oktaAuth } = useOktaAuth() || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');     // free-text filter (client-side)
  const [limit, setLimit] = useState(200);
  const [detailFor, setDetailFor] = useState(null); // row id whose detail JSON is open

  const fetchActivity = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let token = '';
      try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
      const res = await fetch(`/api/admin/deploy-activity?limit=${encodeURIComponent(limit)}`, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.error || `HTTP ${res.status}`);
      setData(body);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [oktaAuth, limit]);

  useEffect(() => { fetchActivity(); }, [fetchActivity]);

  const entries = (data?.entries || []).filter(row => {
    if (!filter) return true;
    const q = filter.toLowerCase();
    return (row.appName || '').toLowerCase().includes(q)
      || (row.actor || '').toLowerCase().includes(q)
      || (row.action || '').toLowerCase().includes(q)
      || (row.outcome || '').toLowerCase().includes(q)
      || (row.message || '').toLowerCase().includes(q);
  });

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>📜 Admin · Deploy Activity</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="search"
            placeholder="Filter by app, actor, action…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={styles.search}
          />
          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            style={styles.select}
            title="How many recent rows to load"
          >
            <option value={100}>Last 100</option>
            <option value={200}>Last 200</option>
            <option value={500}>Last 500</option>
            <option value={1000}>Last 1000</option>
          </select>
          <button onClick={fetchActivity} style={styles.refreshBtn} disabled={loading}>
            {loading ? '…' : '↻ Refresh'}
          </button>
          <AdminMenu />
        </div>
      </div>

      <p style={styles.blurb}>
        Durable audit trail of every deploy, redeploy, delete, stop, start and enhance.
        Use it to trace an app that “went missing” back to who changed it and when.
      </p>

      {error && <div style={styles.error}>⚠️ {error}</div>}

      {data && (
        <div style={styles.summary}>
          Signed in as <strong>{data.currentUser || 'admin'}</strong>
          {' · '}{entries.length} of {data.total} rows shown
        </div>
      )}

      <div style={styles.tableWrap}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>When</th>
              <th style={styles.th}>Action</th>
              <th style={styles.th}>App</th>
              <th style={styles.th}>Actor</th>
              <th style={styles.th}>Outcome</th>
              <th style={styles.th}>Message</th>
              <th style={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && !loading && (
              <tr><td style={styles.empty} colSpan={7}>No activity recorded yet.</td></tr>
            )}
            {entries.map((row) => {
              const as = actionStyle(row.action);
              const hasDetail = row.detail && Object.keys(row.detail).length > 0;
              const open = detailFor === row.id;
              return (
                <React.Fragment key={row.id || `${row.at}-${row.appName}-${row.action}`}>
                  <tr style={styles.tr}>
                    <td style={styles.tdWhen}>{fmtWhen(row.at)}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, background: as.bg, color: as.fg }}>
                        {row.action || '—'}
                      </span>
                    </td>
                    <td style={styles.tdApp}>{row.appName || '—'}</td>
                    <td style={styles.td}>{row.actor || '—'}</td>
                    <td style={styles.td}>{row.outcome || '—'}</td>
                    <td style={styles.tdMsg}>{row.message || ''}</td>
                    <td style={styles.td}>
                      {hasDetail ? (
                        <button
                          type="button"
                          style={styles.detailBtn}
                          onClick={() => setDetailFor(open ? null : row.id)}
                        >
                          {open ? 'Hide' : 'Detail'}
                        </button>
                      ) : null}
                    </td>
                  </tr>
                  {open && hasDetail && (
                    <tr>
                      <td style={styles.detailCell} colSpan={7}>
                        <pre style={styles.detailPre}>{JSON.stringify(row.detail, null, 2)}</pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  page: { padding: 24, maxWidth: 1200, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  title: { fontSize: 22, margin: 0 },
  blurb: { color: '#555', fontSize: 13, marginTop: 8, marginBottom: 12 },
  search: { padding: '7px 10px', border: '1px solid #ccc', borderRadius: 8, fontSize: 13, minWidth: 220 },
  select: { padding: '7px 10px', border: '1px solid #ccc', borderRadius: 8, fontSize: 13 },
  refreshBtn: { padding: '7px 12px', border: '1px solid #ccc', borderRadius: 8, background: '#fff', cursor: 'pointer', fontSize: 13 },
  error: { background: '#ffebee', color: '#b71c1c', padding: '10px 12px', borderRadius: 8, margin: '12px 0', fontSize: 13 },
  summary: { color: '#555', fontSize: 13, margin: '8px 0 12px' },
  tableWrap: { overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 10 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #eee', background: '#fafafa', fontWeight: 600, whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f0f0f0' },
  td: { padding: '8px 12px', verticalAlign: 'top' },
  tdWhen: { padding: '8px 12px', whiteSpace: 'nowrap', color: '#444' },
  tdApp: { padding: '8px 12px', fontWeight: 600, whiteSpace: 'nowrap' },
  tdMsg: { padding: '8px 12px', color: '#333', maxWidth: 420 },
  badge: { display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: 12, fontWeight: 700, textTransform: 'capitalize' },
  detailBtn: { padding: '3px 8px', border: '1px solid #ccc', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12 },
  detailCell: { padding: 0, background: '#fafafa' },
  detailPre: { margin: 0, padding: 12, fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#333' },
  empty: { padding: 20, textAlign: 'center', color: '#888' },
};
